import React, { useEffect, useMemo, useState } from 'react';
import {
  Field,
  Input,
  SpinButton,
  Dropdown,
  Option,
  Button,
  Checkbox,
  Text,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import BaseDialog from '../BaseDialog';
import { apiFetch } from '../../lib/apiFetch';
import { blockNonNumericInput, sanitizeDecimalString } from '../../lib/numericInput';

interface TaxCategory {
  id: string;
  name: string;
  rate: number;
  booqable_id?: string | null;
}

export interface CustomLineSubmitPayload {
  title: string;
  quantity: number;
  priceEachInCents: number;
  taxCategoryId: string | null;
  // Booqable taxes lines via order.tax_region × line.taxable; a line's
  // tax_category_id is inert in TWM's model. `taxExempt` is the only lever that
  // actually suppresses tax — it maps to line taxable=false on the backend.
  taxExempt: boolean;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: CustomLineSubmitPayload) => Promise<void>;
}

const useStyles = makeStyles({
  body: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
  },
  total: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...shorthands.padding('12px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('4px'),
  },
});

const fmtUsd = (cents: number) =>
  ((cents || 0) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export const CustomLineDialog: React.FC<Props> = ({ isOpen, onClose, onSubmit }) => {
  const styles = useStyles();
  const [title, setTitle] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [priceStr, setPriceStr] = useState('');
  const [taxCategoryId, setTaxCategoryId] = useState<string>('');
  const [taxExempt, setTaxExempt] = useState(false);
  const [taxCategories, setTaxCategories] = useState<TaxCategory[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setTitle('');
    setQuantity(1);
    setPriceStr('');
    setTaxCategoryId('');
    setTaxExempt(false);
    setError(null);
    apiFetch('/tax-regions')
      .then((res) => res.json())
      .then((data) => {
        const rows = (data?.data || []) as Array<{ id: string; type: string; attributes: any }>;
        setTaxCategories(
          rows
            .filter((r) => r.type === 'tax_categories')
            .map((r) => ({
              id: r.id,
              name: r.attributes.name,
              rate: r.attributes.rate,
              booqable_id: r.attributes.booqable_id,
            })),
        );
      })
      .catch(() => {});
  }, [isOpen]);

  const priceCents = useMemo(() => {
    const n = parseFloat(priceStr);
    return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : 0;
  }, [priceStr]);
  const totalCents = priceCents * quantity;
  const canSubmit = title.trim().length > 0 && priceCents >= 0 && quantity >= 1 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        title: title.trim(),
        quantity,
        priceEachInCents: priceCents,
        taxCategoryId: taxExempt ? null : taxCategoryId || null,
        taxExempt,
      });
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to add custom line');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCategory = taxCategories.find((c) => c.id === taxCategoryId);

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Add custom line"
      maxWidth="520px"
      actions={
        <>
          <Button appearance="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button appearance="primary" onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? 'Adding…' : 'Add line'}
          </Button>
        </>
      }
    >
      <div className={styles.body}>
        <Field label="Title" required>
          <Input
            size="large"
            value={title}
            onChange={(_, d) => setTitle(d.value)}
            placeholder="e.g. Disposal surcharge"
            autoFocus
          />
        </Field>
        <Field label="Quantity">
          <SpinButton
            size="large"
            value={quantity}
            min={1}
            // Whole-unit count — block non-digits at the input (no decimal).
            input={{
              inputMode: 'numeric',
              onBeforeInput: (e: React.FormEvent<HTMLInputElement>) =>
                blockNonNumericInput(e, false),
            }}
            onChange={(_, d) => setQuantity(typeof d.value === 'number' ? d.value : 1)}
          />
        </Field>
        <Field label="Unit price (USD)">
          <Input
            size="large"
            type="text"
            inputMode="decimal"
            value={priceStr}
            // Controlled value — sanitizing here also covers paste.
            onChange={(_, d) => setPriceStr(sanitizeDecimalString(d.value))}
            placeholder="0.00"
            contentBefore={<Text>$</Text>}
          />
        </Field>
        <Field label="Tax profile">
          <Dropdown
            size="large"
            placeholder="Default"
            disabled={taxExempt}
            value={selectedCategory ? `${selectedCategory.name} (${(selectedCategory.rate * 100).toFixed(2)}%)` : ''}
            selectedOptions={taxCategoryId ? [taxCategoryId] : []}
            onOptionSelect={(_, d) => setTaxCategoryId(d.optionValue || '')}
          >
            <Option value="" text="Default">
              Default (none)
            </Option>
            {taxCategories.map((c) => (
              <Option key={c.id} value={c.id} text={`${c.name} (${(c.rate * 100).toFixed(2)}%)`}>
                {c.name} ({(c.rate * 100).toFixed(2)}%)
              </Option>
            ))}
          </Dropdown>
        </Field>
        <Checkbox
          checked={taxExempt}
          onChange={(_, d) => setTaxExempt(d.checked === true)}
          label="Tax exempt (no tax on this line)"
        />
        <div className={styles.total}>
          <Text weight="semibold">Line total</Text>
          <Text weight="semibold">{fmtUsd(totalCents)}</Text>
        </div>
        {error && (
          <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{error}</Text>
        )}
      </div>
    </BaseDialog>
  );
};

export default CustomLineDialog;
